import { useContext, useEffect } from "react";
import { isRight } from "fp-ts/Either";
import { deliveryRevisionChangedT } from "dora-contracts";
import { SocketContext } from "../../../../SocketConnection";
import { useAppDispatch } from "../../../../redux-store";
import { deliveryRevisionNudged } from "../../../../ducks/data/deliveries";

/**
 * Keeps the open cargo dialog's delivery (its invoice lines) up to date while
 * other people change it. The dialog joins the delivery's socket room; each
 * change the server announces is handed to the deliveries duck, which decides
 * whether to refetch.
 *
 * `deliveryId` is null when there is nothing to keep up to date (a new cargo).
 */
export const useDeliveryLiveUpdates = (deliveryId: string | null) => {
  const socket = useContext(SocketContext);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!socket || !deliveryId) {
      return;
    }
    const join = () => socket.emit("join-delivery-room", { deliveryId });
    const onChanged = (payload: unknown) => {
      const decoded = deliveryRevisionChangedT.decode(payload);
      if (isRight(decoded) && decoded.right.deliveryId === deliveryId) {
        dispatch(
          deliveryRevisionNudged({
            deliveryId,
            revision: decoded.right.revision,
          })
        );
      }
    };
    // A reconnect gets a new socket on the server, without its rooms, and may
    // have missed changes: the revision it missed is unknown.
    const onReconnect = () => {
      join();
      dispatch(deliveryRevisionNudged({ deliveryId, revision: null }));
    };
    join();
    socket.on("delivery.revision-changed", onChanged);
    socket.on("connect", onReconnect);
    return () => {
      socket.off("delivery.revision-changed", onChanged);
      socket.off("connect", onReconnect);
      socket.emit("leave-delivery-room", { deliveryId });
    };
  }, [socket, deliveryId, dispatch]);
};
